import * as React from 'react'

import { useViewCount } from '@/hooks/use-view-count'

import { ViewCountEntityType } from '@/types/view-count.type'

interface ViewTrackerProps {
  entityType: ViewCountEntityType
  entityId: string
  /**
   * The number of hours to track the view for.
   * @default 12 hours
   */
  windowHours?: number
  /**
   * Whether to automatically track the view.
   * @default true
   */
  autoTrack?: boolean
  /**
   * Callback function to be called when the view is successfully tracked.
   */
  onTrackSuccess?: () => void
  /**
   * Callback function to be called when the view is not successfully tracked.
   */
  onTrackError?: (error: string) => void
  /**
   * The children to render.
   */
  children?: React.ReactNode
}

export const ViewTracker: React.FC<ViewTrackerProps> = ({
  entityType,
  entityId,
  windowHours,
  autoTrack = true,
  onTrackSuccess,
  onTrackError,
  children,
}) => {
  const { trackView } = useViewCount()
  const hasTracked = React.useRef(false)

  React.useEffect(() => {
    if (autoTrack && !hasTracked.current) {
      // In dev (React StrictMode) and during fast route transitions, effects can run
      // twice / in parallel. Guard against duplicate network calls that can race the
      // backend dedupe and increment counts twice.
      const dedupeKey = `view-tracker:${entityType}:${entityId}:${windowHours ?? 'default'}`
      const now = Date.now()
      try {
        const prev = globalThis.sessionStorage?.getItem(dedupeKey)
        const prevTs = prev ? Number(prev) : 0
        if (prevTs && Number.isFinite(prevTs) && now - prevTs < 5000) {
          hasTracked.current = true
          return
        }
        globalThis.sessionStorage?.setItem(dedupeKey, String(now))
      } catch {
        // ignore storage errors (private mode, SSR, etc.)
      }

      const trackViewAsync = async () => {
        try {
          await trackView({
            entityType,
            entityId,
            windowHours,
          })
          hasTracked.current = true
          onTrackSuccess?.()
        } catch (err) {
          onTrackError?.(err instanceof Error ? err.message : 'Failed to track view')
        }
      }

      trackViewAsync()
    }
  }, [autoTrack, entityType, entityId, windowHours, trackView, onTrackSuccess, onTrackError])

  if (children) {
    return <>{children}</>
  }

  return null
}

export default ViewTracker
